import { useMemo, useState, type FormEvent } from "react";
import { usePhantomStatus } from "../../auth/usePhantomStatus.ts";
import { useUserProfile } from "../../auth/useUserProfile.ts";
import { OnboardingShell } from "../../components/onboarding/OnboardingShell.tsx";
import * as f from "../../components/onboarding/onboarding-form.styles.ts";
import type { Json } from "../../db/types.ts";
import { TOTAL_ONBOARDING_STEPS, getStepNumber } from "../../onboarding/steps.ts";
import { getBrowserClient } from "../../supabase/browser.ts";
import * as ph from "./phantom-bet.styles.ts";

// ─── Backend contract ────────────────────────────────────────────────────
//
// Mirrors the strict acknowledgment shape validated by
// public.opt_out_of_phantom_bet (Migration 0022). All four flags must be
// present and exactly the values below — server-side will reject anything
// else. activate_phantom_bet takes no arguments.
const OPT_OUT_ACK: Json = {
  typed_confirmation: "I understand",
  phantom_recommended: true,
  understands_real_money_risk: true,
  understands_skipping_7_day_observation: true,
};

const REQUIRED_PHRASE = "I understand";

type View = "choice" | "opt_out_confirm";

export function PhantomBetPage(): JSX.Element {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { refetch: refetchProfile } = useUserProfile();
  const { refetch: refetchPhantom } = usePhantomStatus();

  const [view, setView] = useState<View>("choice");
  const [confirmed, setConfirmed] = useState(false);
  const [typedPhrase, setTypedPhrase] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleStartPhantom(): Promise<void> {
    setError(null);
    setBusy(true);
    const { error: rpcError } = await supabase.rpc("activate_phantom_bet");
    if (rpcError) {
      setError(rpcError.message);
      setBusy(false);
      return;
    }
    await refetchPhantom();
    // AuthGate sees hasActiveSession=true on the next render and redirects
    // to /dashboard (the user is now on an enabled onboarding route with
    // every gate satisfied).
  }

  async function handleConfirmOptOut(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!confirmed) {
      setError("Please tick the box to confirm you understand the risk.");
      return;
    }
    if (typedPhrase.trim() !== REQUIRED_PHRASE) {
      setError(`Type the exact phrase "${REQUIRED_PHRASE}" to skip Phantom Bet.`);
      return;
    }
    setError(null);
    setBusy(true);
    const { error: rpcError } = await supabase.rpc("opt_out_of_phantom_bet", {
      p_acknowledgment: OPT_OUT_ACK,
    });
    if (rpcError) {
      setError(rpcError.message);
      setBusy(false);
      return;
    }
    await refetchProfile();
    // AuthGate sees phantom_opt_out_at !== null on the next render and
    // redirects to /dashboard.
  }

  if (view === "opt_out_confirm") {
    return (
      <OnboardingShell
        stepNumber={getStepNumber("phantom")}
        totalSteps={TOTAL_ONBOARDING_STEPS}
        eyebrow="Skip Phantom Bet"
        title="Are you sure you want to skip Phantom Bet?"
        description={
          "Phantom Bet is the recommended path. It runs the full STRIKZA recommendation flow with " +
          "simulated stakes for seven days so you can observe agent performance before any real " +
          "money is at risk."
        }
      >
        <form style={f.formStack} onSubmit={handleConfirmOptOut} noValidate>
          {error ? <div style={f.message("error")}>{error}</div> : null}

          <div style={ph.warningBlock}>
            <span style={ph.warningEyebrow}>Recommended: do not skip</span>
            <ul style={ph.warningList}>
              <li>
                Skipping means you may face real-money decisions without first observing how the
                STRIKZA agents perform.
              </li>
              <li>
                The seven-day Phantom observation period is designed to protect you against
                following recommendations blindly.
              </li>
              <li>
                Real-stake recommendations remain disabled in Phase 1A regardless of this choice —
                this opt-out only changes how STRIKZA introduces them later.
              </li>
            </ul>
          </div>

          <label style={f.checkboxRow}>
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              style={f.checkboxInput}
            />
            <span>
              I understand Phantom Bet is recommended, that skipping it means I will not observe
              agent performance for seven days, and that I am responsible for the additional risk
              this creates when real-stake recommendations open up later.
            </span>
          </label>

          <div style={f.fieldGroup}>
            <label htmlFor="typed-phrase" style={f.label}>
              Type the phrase &quot;{REQUIRED_PHRASE}&quot; to continue
            </label>
            <input
              id="typed-phrase"
              type="text"
              autoComplete="off"
              value={typedPhrase}
              onChange={(e) => setTypedPhrase(e.target.value)}
              style={f.input}
            />
          </div>

          <div style={f.buttonRow}>
            <button type="submit" style={ph.dangerBtn} disabled={busy}>
              {busy ? "..." : "Skip Phantom Bet"}
            </button>
            <button
              type="button"
              style={f.secondaryBtn}
              disabled={busy}
              onClick={() => {
                setView("choice");
                setConfirmed(false);
                setTypedPhrase("");
                setError(null);
              }}
            >
              Go back
            </button>
          </div>
        </form>
      </OnboardingShell>
    );
  }

  return (
    <OnboardingShell
      stepNumber={getStepNumber("phantom")}
      totalSteps={TOTAL_ONBOARDING_STEPS}
      title="Start with Phantom Bet"
      description={
        "Phantom Bet runs the full STRIKZA recommendation flow with simulated stakes for seven " +
        "days. It lets you observe agent performance before any real money is at stake. Phase 1A " +
        "remains Phantom-only — STRIKZA never places bets on your behalf."
      }
    >
      <div style={f.formStack}>
        {error ? <div style={f.message("error")}>{error}</div> : null}

        <div style={ph.recommendationCallout}>
          <span style={ph.calloutEyebrow}>Recommended</span>
          <span style={ph.calloutBody}>
            STRIKZA strongly recommends starting with Phantom Bet. You can always opt into
            real-stake recommendations after the observation period.
          </span>
        </div>

        <div style={f.buttonRow}>
          <button
            type="button"
            style={f.primaryBtn}
            disabled={busy}
            onClick={() => void handleStartPhantom()}
          >
            {busy ? "..." : "Start Phantom Bet"}
          </button>
          <button
            type="button"
            style={f.secondaryBtn}
            disabled={busy}
            onClick={() => {
              setError(null);
              setView("opt_out_confirm");
            }}
          >
            Skip Phantom Bet
          </button>
        </div>
      </div>
    </OnboardingShell>
  );
}
